"use client"

import Image from "next/image"
import { cn } from "@/lib/utils"

type DirectorSpotlightSectionProps = {
  title: string
  eyebrow?: string
  name: string
  role: string
  image: string
  description: string
  highlights: string[]
  noteTitle?: string
  noteDescription?: string
  className?: string
}

export default function DirectorSpotlightSection({
  title,
  eyebrow = "Conoce a",
  name,
  role,
  image,
  description,
  highlights,
  noteTitle = "Mi pasión",
  noteDescription,
  className,
}: DirectorSpotlightSectionProps) {
  const paragraphs = description
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)

  const [firstParagraph, ...remainingParagraphs] = paragraphs

  return (
    <section className={cn("bg-white py-12 md:py-16", className)}>
      <div className="mx-auto max-w-6xl px-4 md:px-8">
        <div className="mb-8">
          <p
            className="text-[11px] font-medium uppercase tracking-[0.34em] text-zinc-400"
            style={{ fontFamily: "Montserrat, sans-serif" }}
          >
            {eyebrow}
          </p>
          <div className="mt-3 h-[3px] w-full rounded-full bg-[#fb0009]" aria-hidden="true" />
        </div>

        <h2 className="sr-only">{title}</h2>

        <div className="mx-auto max-w-5xl">
          <div className="grid items-start gap-8 md:grid-cols-[220px_minmax(0,1fr)]">
            <div className="relative aspect-square w-full overflow-hidden rounded-[28px] bg-zinc-100">
              <Image
                src={image}
                alt={name}
                fill
                priority
                sizes="(min-width: 1024px) 220px, 100vw"
                className="object-cover object-center"
              />
            </div>

            <div className="flex flex-col gap-5 pt-1 md:pt-2">
              <div className="max-w-4xl">
                <h3
                  className="text-4xl font-extrabold uppercase tracking-tight text-[#fb0009] md:text-[2.9rem]"
                  style={{ fontFamily: "DIN, sans-serif" }}
                >
                  {name}
                </h3>
                <p
                  className="mt-2 text-[1.05rem] font-bold text-zinc-950 md:text-[1.2rem]"
                  style={{ fontFamily: "Montserrat, sans-serif" }}
                >
                  {role}
                </p>
              </div>

              {firstParagraph && (
                <div
                  className="max-w-4xl text-justify text-[0.98rem] leading-6 text-zinc-700 md:text-[1.05rem]"
                  style={{ fontFamily: "Montserrat, sans-serif" }}
                >
                  <p>{firstParagraph}</p>
                </div>
              )}
            </div>
          </div>

          {remainingParagraphs.length > 0 && (
            <div className="mt-6">
              <div
                className="max-w-5xl space-y-5 text-justify text-[0.98rem] leading-6 text-zinc-700 md:text-[1.05rem]"
                style={{ fontFamily: "Montserrat, sans-serif" }}
              >
                {remainingParagraphs.map((paragraph, paragraphIndex) => (
                  <p key={paragraphIndex}>{paragraph}</p>
                ))}
              </div>
            </div>
          )}

          {highlights.length > 0 && (
            <div className="mt-5">
              <ul
                className="space-y-1.5 text-[0.98rem] leading-6 text-zinc-800 md:text-[1.05rem]"
                style={{ fontFamily: "Montserrat, sans-serif" }}
              >
                {highlights.map((highlight) => (
                  <li key={highlight} className="flex items-start gap-2">
                    <span
                      className="mt-[0.55rem] h-2 w-2 shrink-0 rounded-full bg-[#fb0009]"
                      aria-hidden="true"
                    />
                    <span>{highlight}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {noteDescription && (
          <div className="mt-10 rounded-[18px] border border-zinc-200 bg-white px-6 py-6 md:px-8 md:py-8 lg:px-10 lg:py-9">
            <p
              className="text-lg font-bold text-zinc-950"
              style={{ fontFamily: "Montserrat, sans-serif" }}
            >
              {noteTitle}
            </p>
            <p
              className="mt-3 text-justify text-sm leading-7 text-zinc-700 md:text-base"
              style={{ fontFamily: "Montserrat, sans-serif" }}
            >
              {noteDescription}
            </p>
          </div>
        )}
      </div>
    </section>
  )
}
